@if (visible$ | async) {
  <!-- ── Panel ──────────────────────────────────────────────────────────────── -->
  @if (open$ | async) {
    <div class="sw-panel" animate.enter="sw-panel-enter" animate.leave="sw-panel-leave" role="dialog"
      [attr.aria-label]="'support.widget.ariaDialog' | tr">
      <!-- Header -->
      <div class="sw-header">
        @if ((view$ | async) === 'thread') {
          <button class="sw-hbtn" (click)="backToHome()"
            [attr.aria-label]="'support.widget.ariaBack' | tr">
            <ion-icon name="arrow-back-outline"></ion-icon>
          </button>
        }
        <div class="sw-htitles">
          @if ((view$ | async) === 'home') {
            <span class="sw-title">{{ 'support.widget.greeting' | tr }}</span>
            <span class="sw-subtitle">{{ 'support.widget.subtitle' | tr }}</span>
          }
          @if ((view$ | async) === 'thread') {
            <span class="sw-title">{{ activeConversation?.subject || ('support.widget.subjectFallback' | tr) }}</span>
            @if (activeConversation?.status === 'closed') {
              <span class="sw-subtitle">{{ 'support.widget.statusDone' | tr }}</span>
            }
            @if (activeConversation && activeConversation.status !== 'closed') {
              <span class="sw-subtitle">
                {{ 'support.widget.replyHint' | tr }}
              </span>
            }
          }
        </div>
        <button class="sw-hbtn" (click)="close()" [attr.aria-label]="'support.widget.ariaClose' | tr">
          <ion-icon name="close-outline"></ion-icon>
        </button>
      </div>
      <!-- HOME view -->
      @if ((view$ | async) === 'home') {
        <div class="sw-body" animate.enter="sw-home-enter">
          <div class="sw-new">
            <input class="sw-new-input" [(ngModel)]="newSubject" [placeholder]="'support.widget.newPlaceholder' | tr"
              [disabled]="isCreating" (keydown.enter)="onNewSubjectEnter($event)" maxlength="120" />
            <button class="sw-new-btn" (click)="startNew()" [disabled]="isCreating || !newSubject.trim()">
              @if (isCreating) {
                <ion-spinner name="crescent"></ion-spinner>
              }
              @if (!isCreating) {
                <ion-icon name="create-outline"></ion-icon>
                <span>{{ 'support.widget.newStart' | tr }}</span>
              }
            </button>
          </div>
          @if ((conversations$ | async)?.length) {
            <div class="sw-section">{{ 'support.widget.listHeading' | tr }}</div>
          }
          <div class="sw-conv-list">
            @for (c of conversations$ | async; track trackByConv($index, c)) {
              <button class="sw-conv" animate.enter="sw-conv-enter"
                (click)="openConversation(c)" [class.is-closed]="c.status === 'closed'">
                <span class="sw-conv-dot" [class.is-unread]="c.unreadByUser"></span>
                <span class="sw-conv-main">
                  <span class="sw-conv-subject">{{ c.subject }}</span>
                  <span class="sw-conv-preview">{{ c.lastMessagePreview || ('support.widget.previewFallback' | tr) }}</span>
                </span>
                <span class="sw-conv-meta">
                  <span class="sw-conv-time">{{ c.lastMessageAtUtc | date: 'dd.MM.' }}</span>
                  @if (c.status === 'closed') {
                    <ion-icon name="checkmark-done-outline"></ion-icon>
                  }
                </span>
              </button>
            }
            @if (!(conversations$ | async)?.length) {
              <div class="sw-empty">
                <ion-icon name="chatbubbles-outline"></ion-icon>
                <span [innerHTML]="'support.widget.emptyState' | tr"></span>
              </div>
            }
          </div>
        </div>
      }
      <!-- THREAD view -->
      @if ((view$ | async) === 'thread') {
        <div class="sw-thread" animate.enter="sw-thread-enter" (drop)="onDrop($event)"
          (dragover)="onDragOver($event)">
          <div class="sw-messages" #threadScroll (paste)="onPaste($event)">
            <!-- Thread loads in the background — show a 3-dot bubble loader meanwhile. -->
            @if (isLoadingThread) {
              <div class="sw-msg is-admin sw-msg--typing" animate.enter="sw-msg-enter"
                aria-busy="true" [attr.aria-label]="'support.widget.loadingThread' | tr">
                <div class="sw-bubble">
                  <div class="sw-typing">
                    <span class="sw-typing-dot"></span>
                    <span class="sw-typing-dot"></span>
                    <span class="sw-typing-dot"></span>
                  </div>
                </div>
              </div>
            }
            @for (m of messages; track trackByMsg($index, m)) {
              <div class="sw-msg" animate.enter="sw-msg-enter"
                [class.is-own]="m.senderId === currentUserId" [class.is-admin]="m.isAdminMessage">
                <div class="sw-bubble">
                  @if (m.isAdminMessage) {
                    <span class="sw-bubble-sender">{{ 'support.widget.senderLabel' | tr }}</span>
                  }
                  <p class="sw-bubble-text" [innerHTML]="m.content"></p>
                  @if (m.attachments.length) {
                    <div class="sw-bubble-att">
                      @for (a of m.attachments; track a) {
                        <a [href]="a.downloadUrl" target="_blank" rel="noopener"
                          class="sw-att">
                          @if (isImage(a.contentType)) {
                            <img [src]="a.downloadUrl" [alt]="a.fileName"
                              class="sw-att-img" />
                          }
                          @if (!isImage(a.contentType)) {
                            <span class="sw-att-file">
                              <ion-icon name="document-outline"></ion-icon>{{ a.fileName }}
                            </span>
                          }
                        </a>
                      }
                    </div>
                  }
                  @if (m.suggestsClose && activeConversation?.status === 'ai') {
                    <button class="sw-bubble-action"
                      (click)="confirmClose()">
                      <ion-icon name="checkmark-outline"></ion-icon>{{ 'support.widget.closeConfirm' | tr }}
                    </button>
                  }
                  <span class="sw-bubble-time">{{ m.createdAtUtc | date: 'HH:mm' }}</span>
                </div>
              </div>
            }
            <!-- Karl is generating a reply -->
            @if (isKarlTyping) {
              <div class="sw-msg is-admin sw-msg--typing" animate.enter="sw-msg-enter">
                <div class="sw-bubble">
                  <span class="sw-bubble-sender">{{ 'support.widget.senderLabel' | tr }}</span>
                  <div class="sw-typing" [attr.aria-label]="'support.widget.generating' | tr">
                    <span class="sw-typing-dot"></span>
                    <span class="sw-typing-dot"></span>
                    <span class="sw-typing-dot"></span>
                  </div>
                </div>
              </div>
            }
          </div>
          <!-- Pending attachments -->
          @if (pendingAttachments.length) {
            <div class="sw-pending">
              @for (pa of pendingAttachments; track trackByAtt($index, pa)) {
                <div class="sw-pending-item" animate.enter="sw-chip-enter" animate.leave="sw-chip-leave">
                  <ion-icon [name]="isImage(pa.contentType) ? 'image-outline' : 'document-outline'"></ion-icon>
                  <span class="sw-pending-name">{{ pa.fileName }}</span>
                  @if (pa.status === 'uploading') {
                    <ion-spinner name="crescent"></ion-spinner>
                  }
                  @if (pa.status !== 'uploading') {
                    <button class="sw-pending-remove"
                      (click)="removeAttachment(pa)" [attr.aria-label]="'support.widget.ariaRemove' | tr">
                      <ion-icon name="close-outline"></ion-icon>
                    </button>
                  }
                </div>
              }
            </div>
          }
          <!-- Escalate to human support team (AI conversations only) -->
          @if (isAiConversation) {
            <div class="sw-escalate-bar">
              <button class="sw-escalate-btn" (click)="escalate()" [disabled]="isEscalating">
                @if (isEscalating) {
                  <ion-spinner name="crescent"></ion-spinner>
                }
                @if (!isEscalating) {
                  <ion-icon name="people-outline"></ion-icon>
                  <span>{{ 'support.widget.escalate' | tr }}</span>
                }
              </button>
            </div>
          }
          <!-- Composer -->
          @if (activeConversation && activeConversation.status !== 'closed') {
            <div class="sw-composer">
              <button class="sw-attach" (click)="openFilePicker()" [attr.aria-label]="'support.widget.ariaAttach' | tr">
                <ion-icon name="attach-outline"></ion-icon>
              </button>
              <textarea class="sw-input" [(ngModel)]="messageText"
                [placeholder]="'support.widget.composerPlaceholder' | tr" rows="1"
              (keydown.enter)="onComposerEnter($event)"></textarea>
              <button class="sw-send" (click)="send()" [attr.aria-label]="'support.widget.ariaSend' | tr"
                [disabled]="isSending || (!messageText.trim() && !pendingAttachments.length)">
                <ion-icon name="send"></ion-icon>
              </button>
              <input #fileInput type="file" multiple accept="image/*,application/pdf,text/plain" hidden
                (change)="onFileSelected($event)" />
            </div>
          }
          @if (activeConversation?.status === 'closed') {
            <div class="sw-closed-note">
              {{ 'support.widget.closedNote' | tr }}
            </div>
          }
        </div>
      }
    </div>
  }
  <!-- ── Launcher ───────────────────────────────────────────────────────────── -->
  <button class="sw-launcher" [class.is-open]="open$ | async" (click)="toggle()"
    [attr.aria-label]="'support.widget.ariaLauncher' | tr">
    <ion-icon [name]="(open$ | async) ? 'chevron-down-outline' : 'chatbubbles'"></ion-icon>
    @if (((unreadCount$ | async) ?? 0) > 0 && (open$ | async) === false) {
      <span class="sw-launcher-badge"
      >{{ unreadCount$ | async }}</span>
    }
  </button>
}
